import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ContentCard from "@/components/feature/ContentCard";
import { categories } from "@/mocks/content";
import { useSEO } from "@/hooks/useSEO";
import { isContentType, typeLabels } from "@/lib/content";
import {
  collectTags,
  collectYears,
  filterContents,
  getContentsByType,
  sortContents,
} from "@/lib/content";
import FilterSidebar from "@/pages/category/components/FilterSidebar";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const validType = isContentType(type) ? type : "video";
  const category = categories.find((c) => c.type === validType) ?? categories[0];

  const [duration, setDuration] = useState("all");
  const [year, setYear] = useState("all");
  const [tag, setTag] = useState("all");
  const [sort, setSort] = useState<"latest" | "hot">("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);

  useSEO({
    title: `${category.name} - 羞羞网站`,
    description: `${category.description} 浏览羞羞网站精选的${category.name}，发现更多让你会心一笑的内容。`,
    keywords: `羞羞网站,${category.name},${category.tagline},娱乐内容`,
  });

  const years = useMemo(() => collectYears(validType), [validType]);
  const tags = useMemo(() => collectTags(validType), [validType]);

  useEffect(() => {
    setDuration("all");
    setYear("all");
    setTag("all");
    setSort("latest");
  }, [validType]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [duration, year, tag, sort, validType]);

  const results = useMemo(() => {
    const base = getContentsByType(validType);
    const filtered = filterContents(base, { duration, year, tag });
    return sortContents(filtered, sort);
  }, [validType, duration, year, tag, sort]);

  const shown = results.slice(0, visible);
  const resetFilters = () => {
    setDuration("all");
    setYear("all");
    setTag("all");
  };

  const sidebar = (
    <FilterSidebar
      years={years}
      tags={tags}
      duration={duration}
      year={year}
      tag={tag}
      showDuration={validType === "video"}
      onDuration={setDuration}
      onYear={setYear}
      onTag={setTag}
      onReset={resetFilters}
    />
  );

  return (
    <div className="w-full bg-background-50">
      <Navbar />
      <main className="pt-16">
        <section className="w-full px-4 md:px-8 pt-8 pb-6 bg-background-100 border-b border-background-200">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-5 flex-wrap">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span>内容分类</span>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-900 font-medium">{typeLabels[validType]}</span>
          </nav>

          <div className="flex items-start gap-4">
            <span className="w-14 h-14 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0">
              <i className={`${category.icon} text-2xl`} />
            </span>
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                {category.name}
              </h1>
              <p className="text-sm text-foreground-600 mt-2 max-w-2xl">{category.description}</p>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24 rounded-lg bg-background-100 border border-background-200 p-6">
                {sidebar}
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-600">
                    共 <strong className="text-foreground-950">{results.length}</strong> 条内容
                  </span>
                  <button
                    type="button"
                    onClick={() => setFilterOpen(true)}
                    className="lg:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs font-medium text-foreground-700"
                  >
                    <i className="ri-filter-3-line" />
                    筛选
                  </button>
                </div>

                <div className="flex items-center gap-1 rounded-full bg-background-100 border border-background-200 px-1 py-1">
                  <button
                    type="button"
                    onClick={() => setSort("latest")}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      sort === "latest" ? "bg-primary-500 text-background-50" : "text-foreground-600"
                    }`}
                  >
                    最新
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("hot")}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      sort === "hot" ? "bg-primary-500 text-background-50" : "text-foreground-600"
                    }`}
                  >
                    最热
                  </button>
                </div>
              </div>

              {results.length === 0 ? (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-20 flex flex-col items-center text-center px-6">
                  <span className="w-16 h-16 rounded-full bg-secondary-100 text-secondary-900 flex items-center justify-center">
                    <i className="ri-emotion-unhappy-line text-3xl" />
                  </span>
                  <h3 className="font-heading font-semibold text-lg text-foreground-950 mt-5">
                    没有找到符合条件的内容
                  </h3>
                  <p className="text-sm text-foreground-600 mt-2 max-w-md">
                    试试放宽筛选条件，或者重置后重新浏览吧。
                  </p>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-refresh-line" />
                    重置筛选条件
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {shown.map((item) => (
                      <ContentCard key={item.id} item={item} />
                    ))}
                  </div>

                  {visible < results.length && (
                    <div className="flex justify-center mt-10">
                      <button
                        type="button"
                        onClick={() => setVisible((v) => v + PAGE_SIZE)}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                      >
                        加载更多内容
                        <i className="ri-arrow-down-line" />
                      </button>
                    </div>
                  )}
                  {visible >= results.length && results.length > PAGE_SIZE && (
                    <p className="text-center text-xs text-foreground-500 mt-10">
                      已经到底啦，去别的分类逛逛吧
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {filterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setFilterOpen(false)} />
          <div className="absolute left-0 right-0 bottom-0 bg-background-50 rounded-t-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <span className="font-heading font-semibold text-foreground-950">筛选</span>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setFilterOpen(false)}
                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-600"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            {sidebar}
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-6 w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap"
            >
              查看 {results.length} 条结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}